/**
 * Home page
 * - Sets up the Home page layout and page-specific tweaks.
 * - Adds non-visual SEO (meta + FAQ JSON-LD) to improve search and AI visibility.
 */

import React, { useEffect } from 'react'
import HomeHero from '../components/home/Hero'
import Pillars from '../components/home/Pillars'
import TeamSection from '../components/home/Team'
import Seo from '../components/Seo'

/**
 * Central logo URL used for the hero watermark
 */
const LOGO_URL =
  'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/f2dcbeba-4716-4434-8b8b-01acce34f085.png'

/**
 * Removes the "WHAT WE DO" label from the DOM on Home only.
 * We avoid refactoring unknown components and ensure nothing else changes visually.
 */
function useHideWhatWeDo(): void {
  useEffect(() => {
    const TARGET = 'WHAT WE DO'

    /**
     * Hide any element whose direct text equals TARGET (case-insensitive)
     */
    const sweep = (): void => {
      try {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
        const toHide: HTMLElement[] = []
        let n: Node | null = walker.nextNode()

        while (n) {
          const text = (n as Text).data?.trim()
          if (text && text.toUpperCase() === TARGET) {
            const el = (n as Text).parentElement
            if (el) {
              // Hide the closest small container (button/span/div) to remove the pill with the text.
              const container =
                (el.closest('button, a, span, div') as HTMLElement | null) ?? (el as HTMLElement)
              if (container && !(container as any).dataset.hiddenWhatWeDo) {
                toHide.push(container)
              }
            }
          }
          n = walker.nextNode()
        }

        toHide.forEach((el) => {
          el.style.display = 'none'
          el.setAttribute('aria-hidden', 'true')
          ;(el as any).dataset.hiddenWhatWeDo = 'true'
        })
      } catch {
        // No-op if not available (SSR or restricted env).
      }
    }

    // Initial sweep after mount
    sweep()
    // Observe changes to keep it hidden if the section re-renders
    const observer = new MutationObserver(() => sweep())
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])
}

/**
 * Home-only tweak for Team section sizing and spacing.
 * - Detects grid containers that include multiple circular avatar images (team grid heuristic).
 * - Scales avatars to 90% using CSS transform (no layout shift).
 * - Increases grid gap slightly to add breathing room between cards.
 */
function useTweakTeamSizing(): void {
  useEffect(() => {
    /**
     * Returns true if the image looks circular based on computed border radius
     */
    const isCircular = (img: HTMLImageElement): boolean => {
      const cs = getComputedStyle(img)
      const br = cs.borderRadius || ''
      const val = parseFloat(br)
      const hasPercent = br.includes('%')
      const minDim = Math.min(img.clientWidth || 0, img.clientHeight || 0)

      if (hasPercent) return val >= 40 // e.g., 50% border radius
      if (!Number.isFinite(val) || minDim === 0) return false
      return val >= minDim * 0.4 // large rounded radii likely circular
    }

    const SCALE = 0.9
    const GAP = '1.25rem'

    /**
     * Apply adjustments only to likely team grids
     */
    const sweep = (): void => {
      try {
        // Find all grid-like containers
        const containers = Array.from(document.querySelectorAll<HTMLElement>('section,div,ul,ol'))

        const teamGrids = containers.filter((el) => {
          const cs = getComputedStyle(el)
          if (cs.display !== 'grid') return false
          const imgs = Array.from(el.querySelectorAll<HTMLImageElement>('img'))
          const circularCount = imgs.filter(isCircular).length
          // Heuristic: team grid typically has several circular avatars
          return circularCount >= 3
        })

        teamGrids.forEach((grid) => {
          if (!(grid as any).dataset.teamSpacingPatched) {
            grid.style.gap = GAP // add a bit more space
            ;(grid as any).dataset.teamSpacingPatched = 'true'
          }

          // Scale only the circular avatars within this grid
          const avatars = Array.from(grid.querySelectorAll<HTMLImageElement>('img')).filter(isCircular)
          avatars.forEach((img) => {
            const el = img as HTMLImageElement & { dataset: Record<string, string> }
            if (!el.dataset.teamAvatarScaled) {
              el.style.transform = `scale(${SCALE})`
              el.style.transformOrigin = 'center center'
              el.dataset.teamAvatarScaled = 'true'
            }
          })
        })
      } catch {
        // Silently ignore if not available
      }
    }

    // Initial run after mount
    const init = (): void => {
      // Defer to allow layout to settle for accurate measurements
      requestAnimationFrame(() => setTimeout(sweep, 0))
    }
    init()

    // Keep it consistent if the Team section re-renders
    const observer = new MutationObserver(() => sweep())
    observer.observe(document.body, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [])
}

/**
 * Home page component with SEO integration
 */
export default function HomePage(): JSX.Element {
  // Home-only effect to remove the "WHAT WE DO" label
  useHideWhatWeDo()
  // Home-only effect to gently reduce avatar size and increase card spacing in the team grid
  useTweakTeamSizing()

  // User-provided exact subtitle (applied verbatim)
  const heroSubtitle =
    "Boost your brand in Iraq & Kurdistan with expert media planning, digital ads, and influencer marketing services which designed for maximum reach and impact and trusted by global and local brands for over 17 years."

  /**
   * FAQ JSON-LD for the Home page
   */
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What services does Edicoo provide?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Edicoo provides a full range of advertising services including media planning & buying, digital marketing, influencer marketing, content creation, translation, and campaign monitoring.'
        }
      },
      {
        '@type': 'Question',
        name: 'Which regions does Edicoo serve?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Edicoo serves clients across Iraq, including Erbil, Baghdad and wider Kurdistan regions, and supports campaigns for both Kurdish and Arabic audiences.'
        }
      },
      {
        '@type': 'Question',
        name: 'How can I contact Edicoo for a campaign?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can reach out via the Contact page, phone or email. Our senior team typically responds within business hours and we offer rapid initial consultations.'
        }
      }
    ]
  }

  return (
    <>
      <Seo
        title="Edicoo — Advertising, Media Planning & Digital Marketing"
        description="Edicoo Agency: Media planning, buying, digital advertising, influencer marketing and creative services across Iraq & Kurdistan. Trusted by local and global brands."
        url="https://your-site.com"
        image={LOGO_URL}
        jsonLd={[faqJsonLd]}
      />
      <div className="w-full">
        <HomeHero
          logoUrl={LOGO_URL}
          bgImageUrl="https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/43ca0b48-5bcb-4b46-8a60-b64030c1a09e.png"
          showWatermark
          subtitle={heroSubtitle}
        />
        <Pillars />
        <TeamSection />
      </div>
    </>
  )
}